import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HxBrandSelectComponent, HxPagedListDirective } from 'hx-components';
import { BrandModel, HxCategoryService, HxUiLabelPipe, PagedList, ProductCategoryModel } from 'hx-services';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { FormsModule } from '@angular/forms';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NgFor } from '@angular/common';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';

interface ProductCategory extends ProductCategoryModel {
  brand?: BrandModel;
}

@Component({
    selector: 'hc-product-category-list',
    templateUrl: './product-category-list.component.html',
    styleUrls: ['./product-category-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, HxBrandSelectComponent, FormsModule, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class ProductCategoryListComponent extends HxPagedListDirective<ProductCategory> implements OnInit {
  isEdit = false;

  query = '';
  filter: {
    brandId?: number;
  } = {};

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    protected categoryService: HxCategoryService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
        this.filter.brandId = asNumberFn('brandId');
        super.load(paramMap);
      });
  }

  applyFilters() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: {
        brandId: this.filter.brandId,
      },
      queryParamsHandling: 'merge',
    });
  }

  async remove(id: number): Promise<void> {
    await this.categoryService.removeCategory(id);
    this.loadData();
    this.notification.success(this.tr.translate('nav.ProductCategories'), this.tr.translate('nt.dataRemoved'));
  }

  protected async loadList(): Promise<PagedList<ProductCategory>> {
    const paged = await this.categoryService.getCategories({
      limit: this.limit,
      page: this.page,
      query: this.query,
      classNames: ['Brand'],
      includeDisabled: true,
      brandId: this.filter.brandId,
    });
    if (paged.included) {
      const brandMap = new Map((paged.included['Brand'] as BrandModel[]).map(i => [i.id, i]));
      paged.list = paged.list.map(pi => ({
        ...pi,
        brand: brandMap.get(pi.brandId)
      } as ProductCategory));
    }
    return paged as unknown as PagedList<ProductCategory>;
  }
}
